import React from "react";
import { Bar } from "react-chartjs-2";
import annotationPlugin from 'chartjs-plugin-annotation';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { ChartOptions } from "chart.js";
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin // Register the annotation plugin
);


interface ChartProps {
  data: {"label" : string[], "value": number[]};
  lines?: [number, number];
  minLabels?: number[];
  is_log? : boolean;
}

// Helper function to format numbers with superscript exponents for Chart.js
const formatWithPrettyExponents = (value: string | number, useLogFormat: boolean): string => {
  // Only apply pretty formatting if is_log is true
  if (!useLogFormat) {
    // Standard formatting without exponents
    if (typeof value === 'string' && value.includes(' - ')) {
      const parts = value.split(' - ');
      const formattedParts = parts.map(part => {
        const num = Number(part);
        if (!isNaN(num)) {
          return Math.abs(num) > 1000000 ? num.toExponential(2) : num.toFixed(2);
        }
        return part; // Keep as is if not a number
      });
      return formattedParts.join(" - ");
    }
    
    const num = Number(value);
    if (isNaN(num)) return String(value);
    return Math.abs(num) > 1000000 ? num.toExponential(2) : num.toFixed(2);
  }
  
  // Unicode superscript characters for exponents
  const superscriptMap: {[key: string]: string} = {
    '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', 
    '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
    '-': '⁻', '.': '·'
  };
  
  // Convert a number to superscript string
  const toSuperscript = (num: number): string => {
    const numStr = num.toFixed(2);
    return [...numStr].map(char => superscriptMap[char] || char).join('');
  };
  
  // Check if the label appears to be a simple negative number range (like -2.197 - -1.81)
  // rather than an exponent range
  if (typeof value === 'string' && value.includes(' - ')) {
    const parts = value.split(' - ');
    
    // Check if both parts are numbers
    if (parts.every(part => !isNaN(Number(part)))) {
      const numbers = parts.map(Number);
      
      // Process each part and format with exponents
      const formattedParts = numbers.map(num => {
        return `10${toSuperscript(num)}`;
      });
      
      return formattedParts.join(" - ");
    }
  }
  
  // For single values
  const num = Number(value);
  if (isNaN(num)) return String(value); // Not a number, return as is
  
  return `10${toSuperscript(num)}`;
};

const Histogram: React.FC<ChartProps> = ({ data, lines, minLabels, is_log }) => {

  const generateAnnotations = () => {
    if (!lines || !minLabels) return [];

    return lines.map((value) => {
      let index = 0;
      for (let i = 0; i < minLabels.length; i++) {
        if (value >= minLabels[i]) {
          index = i;
        }
      }

      return {
        type: "line" as const,
        mode: "vertical",
        scaleID: "x",
        value: index,
        borderColor: "red",
        borderWidth: 2,
      };
    });
  };

  const chartData = {
    labels: data.label,
    datasets: [
      {
        label: "Frequency",
        data: data.value,
        backgroundColor: "rgba(75, 192, 192, 0.6)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 1,
      },
    ],
  };

  // Chart.js options with enhanced formatting
  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        bottom: 10 // Add padding for the labels
      }
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 45, // Rotate labels for better readability
          minRotation: 45, // Ensure consistent rotation
          callback: function (value, index) {
            let label = data.label[index]; // Get the corresponding label
            
            // Check if label contains a range
            if (label && label.includes(' - ')) {
              return formatWithPrettyExponents(label, is_log === true);
            }
            
            return label; // Return formatted label
          },
        },
      },
      y: {
        ticks: {
          callback: function (value) {
            // Always use standard number formatting for Y-axis
            return Number(value).toLocaleString(); // Format with commas for readability
          },
        },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = context.raw as number;
            return `Frequency: ${value.toLocaleString()}`;
          },
          title: function(tooltipItems) {
            const index = tooltipItems[0].dataIndex;
            const label = data.label[index];
            
            if (label.search(' - ') > 0) {
              return formatWithPrettyExponents(label, is_log === true);
            }
            
            return label;
          }
        }
      },
      legend: {
        display: false,
      },
      annotation: {
        annotations: generateAnnotations(), // Add annotations dynamically
      },
    },
  };

  return (
    <div style={{ height: '300px', width: '100%' }}>
      <Bar data={chartData} options={options} />
    </div>
  );
};

export default Histogram;